import React from 'react';

/**
 * White Rabbit Academy - Cyberpunk Glass Morphism Blur Effects
 * Comprehensive blur utility system for consistent application across all UI components
 */

export type BlurIntensity = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'custom';
export type BlurType = 'backdrop' | 'glass' | 'overlay' | 'navigation' | 'modal' | 'toast';

interface BlurConfig {
  blur: string;
  saturation?: string;
  background?: string;
  border?: string;
  className: string;
}

/**
 * Core blur configurations following cyberpunk glass morphism aesthetic
 */
const BLUR_CONFIGS: Record<BlurType, Record<BlurIntensity, BlurConfig>> = {
  backdrop: {
    xs: {
      blur: 'blur(2px)',
      saturation: 'saturate(1.1)',
      className: 'backdrop-blur-xs backdrop-saturate-110',
    },
    sm: {
      blur: 'blur(4px)',
      saturation: 'saturate(1.2)',
      className: 'backdrop-blur-sm backdrop-saturate-120',
    },
    md: {
      blur: 'blur(16px)',
      saturation: 'saturate(180%)',
      className: 'backdrop-blur-md backdrop-saturate-180',
    },
    lg: {
      blur: 'blur(24px)',
      saturation: 'saturate(200%)',
      className: 'backdrop-blur-lg backdrop-saturate-200',
    },
    xl: {
      blur: 'blur(40px)',
      saturation: 'saturate(220%)',
      className: 'backdrop-blur-[40px] backdrop-saturate-[220%]',
    },
    custom: {
      blur: 'blur(1px)',
      saturation: 'saturate(1.2)',
      className: 'backdrop-optimized',
    },
  },
  glass: {
    xs: {
      blur: 'blur(8px)',
      saturation: 'saturate(160%)',
      background: 'rgba(17, 25, 40, 0.15)',
      border: 'rgba(255, 255, 255, 0.1)',
      className: 'bg-glass-light border-glass-light backdrop-blur-sm',
    },
    sm: {
      blur: 'blur(12px)',
      saturation: 'saturate(170%)',
      background: 'rgba(17, 25, 40, 0.20)',
      border: 'rgba(255, 255, 255, 0.125)',
      className: 'bg-glass-medium border-glass-medium backdrop-blur-md',
    },
    md: {
      blur: 'blur(16px)',
      saturation: 'saturate(180%)',
      background: 'rgba(17, 25, 40, 0.25)',
      border: 'rgba(255, 255, 255, 0.125)',
      className: 'glass',
    },
    lg: {
      blur: 'blur(20px)',
      saturation: 'saturate(190%)',
      background: 'rgba(17, 25, 40, 0.30)',
      border: 'rgba(255, 255, 255, 0.15)',
      className: 'bg-glass-heavy border-glass-heavy backdrop-blur-lg',
    },
    xl: {
      blur: 'blur(24px)',
      saturation: 'saturate(200%)',
      background: 'rgba(17, 25, 40, 0.35)',
      border: 'rgba(255, 255, 255, 0.2)',
      className: 'bg-glass-heavy border-glass-heavy backdrop-blur-xl',
    },
    custom: {
      blur: 'blur(1px)',
      saturation: 'saturate(1.2)',
      background: 'rgba(17, 25, 40, 0.1)',
      border: 'rgba(255, 255, 255, 0.05)',
      className: 'backdrop-optimized bg-glass-light/50',
    },
  },
  overlay: {
    xs: { blur: 'blur(2px)', className: 'bg-black/20 backdrop-blur-xs' },
    sm: { blur: 'blur(4px)', className: 'bg-black/30 backdrop-blur-sm' },
    md: { blur: 'blur(8px)', className: 'bg-black/50 backdrop-blur-md' },
    lg: { blur: 'blur(16px)', className: 'bg-black/60 backdrop-blur-lg' },
    xl: { blur: 'blur(24px)', className: 'bg-black/70 backdrop-blur-xl' },
    custom: { blur: 'blur(1px)', className: 'bg-black/40 backdrop-blur-xs' },
  },
  navigation: {
    xs: { blur: 'blur(8px)', className: 'bg-glass-light/80 backdrop-blur-sm border-glass-light' },
    sm: { blur: 'blur(12px)', className: 'bg-glass-light backdrop-blur-md border-glass-medium' },
    md: { blur: 'blur(16px)', className: 'bg-glass-light backdrop-blur-md border-glass-medium' },
    lg: { blur: 'blur(20px)', className: 'bg-glass-medium backdrop-blur-lg border-glass-heavy' },
    xl: { blur: 'blur(40px)', className: 'bg-black/70 backdrop-blur-[40px] border-glass-heavy' },
    custom: { blur: 'blur(1px)', className: 'backdrop-optimized bg-glass-light' },
  },
  modal: {
    xs: { blur: 'blur(4px)', className: 'bg-glass-medium/80 backdrop-blur-sm border-glass-medium' },
    sm: { blur: 'blur(8px)', className: 'bg-glass-medium backdrop-blur-md border-glass-medium' },
    md: { blur: 'blur(16px)', className: 'bg-glass-medium backdrop-blur-lg border-glass-heavy' },
    lg: { blur: 'blur(24px)', className: 'bg-glass-heavy backdrop-blur-xl border-glass-heavy' },
    xl: { blur: 'blur(32px)', className: 'bg-glass-heavy backdrop-blur-[32px] border-glass-heavy' },
    custom: { blur: 'blur(1px)', className: 'backdrop-optimized bg-glass-medium' },
  },
  toast: {
    xs: { blur: 'blur(8px)', className: 'backdrop-blur-sm bg-glass-light border-glass-light' },
    sm: { blur: 'blur(12px)', className: 'backdrop-blur-md bg-glass-medium border-glass-medium' },
    md: { blur: 'blur(16px)', className: 'backdrop-blur-lg bg-glass-medium border-glass-heavy' },
    lg: { blur: 'blur(20px)', className: 'backdrop-blur-xl bg-glass-heavy border-glass-heavy' },
    xl: { blur: 'blur(24px)', className: 'backdrop-blur-[24px] bg-glass-heavy border-glass-heavy' },
    custom: { blur: 'blur(1px)', className: 'backdrop-optimized bg-glass-medium' },
  },
};

/**
 * Get blur configuration for a specific type and intensity
 */
export function getBlurConfig(type: BlurType, intensity: BlurIntensity = 'md'): BlurConfig {
  return BLUR_CONFIGS[type][intensity];
}

/**
 * Get CSS classes for a specific blur effect
 */
export function getBlurClasses(type: BlurType, intensity: BlurIntensity = 'md'): string {
  return BLUR_CONFIGS[type][intensity].className;
}

/**
 * Get inline styles for custom blur implementation
 */
export function getBlurStyles(
  type: BlurType,
  intensity: BlurIntensity = 'md',
): React.CSSProperties {
  const config = BLUR_CONFIGS[type][intensity];

  const styles: React.CSSProperties = {
    backdropFilter: `${config.blur} ${config.saturation || ''}`.trim(),
    WebkitBackdropFilter: `${config.blur} ${config.saturation || ''}`.trim(),
  };

  if (config.background) {
    styles.backgroundColor = config.background;
  }

  if (config.border) {
    styles.borderColor = config.border;
  }

  return styles;
}

/**
 * Utility functions for specific use cases
 */
export const BlurUtils = {
  // Navigation blur (used in PrivateNav, PublicNav)
  navigation: (intensity: BlurIntensity = 'md') => getBlurClasses('navigation', intensity),

  // Glass morphism for cards, containers
  glass: (intensity: BlurIntensity = 'md') => getBlurClasses('glass', intensity),

  // Backdrop blur for overlays
  backdrop: (intensity: BlurIntensity = 'md') => getBlurClasses('backdrop', intensity),

  // Modal/dialog blur
  modal: (intensity: BlurIntensity = 'md') => getBlurClasses('modal', intensity),

  // Toast notification blur
  toast: (intensity: BlurIntensity = 'md') => getBlurClasses('toast', intensity),

  // Overlay blur (for dropdowns, menus)
  overlay: (intensity: BlurIntensity = 'md') => getBlurClasses('overlay', intensity),

  // Performance optimized blur for heavy UI
  optimized: () => 'backdrop-optimized',

  // Custom blur with specific pixel value
  custom: (pixels: number) => `backdrop-blur-[${pixels}px]`,

  // Combine multiple blur effects
  combine: (...effects: string[]) => effects.join(' '),
};

/**
 * React hook for dynamic blur effects
 */
export function useBlurEffect(
  type: BlurType,
  intensity: BlurIntensity = 'md',
  condition: boolean = true,
) {
  return condition ? getBlurClasses(type, intensity) : '';
}

/**
 * CSS-in-JS style generator for dynamic blur
 */
export function createBlurStyle(
  type: BlurType,
  intensity: BlurIntensity = 'md',
  additionalStyles: React.CSSProperties = {},
): React.CSSProperties {
  return {
    ...getBlurStyles(type, intensity),
    ...additionalStyles,
  };
}

/**
 * Preset combinations for common UI patterns
 */
export const BlurPresets = {
  // Navigation patterns
  navbar: 'bg-glass-light backdrop-blur-md border-b border-glass-medium',
  navbarDropdown: 'bg-black/70 backdrop-blur-[40px] border-t border-glass-heavy shadow-2xl',

  // Card patterns
  cardLight: 'bg-glass-light backdrop-blur-sm border border-glass-light',
  cardMedium: 'bg-glass-medium backdrop-blur-md border border-glass-medium',
  cardHeavy: 'bg-glass-heavy backdrop-blur-lg border border-glass-heavy',

  // Modal patterns
  modalOverlay: 'bg-black/80 backdrop-blur-sm',
  modalContent: 'bg-glass-medium backdrop-blur-lg border border-glass-heavy',

  // Toast patterns
  toastDefault: 'backdrop-blur-lg bg-glass-medium border-glass-heavy',
  toastError: 'backdrop-blur-lg bg-red-900/90 border border-red-500',

  // Form patterns
  input: 'bg-glass-medium border-glass-heavy backdrop-blur-sm',
  inputFocus: 'bg-glass-medium border-cyber-cyan backdrop-blur-md',

  // Button patterns
  buttonGlass: 'bg-glass-light hover:bg-glass-medium backdrop-blur-sm border border-glass-light',
  buttonSolid:
    'bg-cyber-cyan/20 hover:bg-cyber-cyan/30 backdrop-blur-md border border-cyber-cyan/50',
};
